import { useState } from "react";
import { Users, Swords } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Modal } from "./shared";

export function RepairSlotsPanel() {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [paBusy, setPaBusy] = useState(false);
  const [paResult, setPaResult] = useState(null);

  const repair = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/repair-slots");
      const n = r.data.repaired_count || 0;
      setResult(r.data);
      setConfirm(false);
      toast.success(n === 0 ? "All slots already consistent — nothing to repair." : `Repaired ${n} account${n === 1 ? "" : "s"}.`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const repairParAequa = async () => {
    setPaBusy(true);
    try {
      const r = await api.post("/admin/repair-par-aequa");
      setPaResult(r.data);
      const { flagged = 0, deleted = 0 } = r.data;
      toast.success(flagged + deleted === 0 ? "No stray Par Aequa temps found — rosters are clean." : `Flagged ${flagged}, removed ${deleted} stale Par Aequa temp(s).`);
    } catch (e) { toast.error(apiErr(e)); } finally { setPaBusy(false); }
  };

  return (
    <div data-testid="repair-slots-panel" className="mb-6 border border-sky-700/40 bg-gradient-to-br from-sky-950/20 to-zinc-950 p-4 rounded-sm">
      <div className="flex items-center gap-2 mb-3">
        <Users size={16} className="text-sky-400" />
        <h3 className="font-cinzel text-sky-300 uppercase tracking-widest text-sm">Reconcile Gladiator Slots</h3>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-zinc-500 flex-1 min-w-[200px]">
          Retroactively ensures every user's <span className="text-sky-300">purchased slots</span> match what they actually paid for, and that admin-awarded slots correctly raise the roster cap and reduce their buyable count. <span className="text-zinc-400">Restore-only — never removes anyone's capacity.</span>
        </p>
        <button data-testid="repair-slots-btn" onClick={() => setConfirm(true)} disabled={busy}
          className="px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-sky-500 text-zinc-950 hover:bg-sky-400 disabled:opacity-50">
          {busy ? "Reconciling…" : "Run Reconcile"}
        </button>
      </div>

      {result && (
        <div data-testid="repair-slots-result" className="mt-4 pt-3 border-t border-sky-900/30 text-sm">
          <p className="text-zinc-300 mb-2">
            Scanned <span className="text-sky-300 font-semibold">{result.scanned}</span> users
            ({result.with_purchases} with purchases) · repaired <span className="text-sky-300 font-semibold">{result.repaired_count}</span>.
          </p>
          {(result.repaired || []).length > 0 && (
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {result.repaired.map((it, i) => (
                <li key={i} className="text-xs text-zinc-500 tabular-nums">
                  {it.name || it.email || "Unknown"} · slots {it.old_extra} → <span className="text-sky-300">{it.new_extra}</span>
                  {it.awarded ? ` (+${it.awarded} awarded)` : ""} · cap {it.roster_cap}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {confirm && (
        <Modal title="Confirm Slot Reconcile" testid="repair-slots-confirm" onClose={() => setConfirm(false)}>
          <p className="text-sm text-zinc-300">Recompute purchased gladiator slots for every user from their completed purchases? This only ever restores slots — it never reduces anyone's roster capacity. Admin-awarded bonus slots are left untouched.</p>
          <div className="mt-5 flex gap-2 justify-end">
            <button data-testid="repair-slots-cancel" onClick={() => setConfirm(false)} disabled={busy} className="px-4 py-2 text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500 rounded-sm">Cancel</button>
            <button data-testid="repair-slots-confirm-btn" onClick={repair} disabled={busy} className="px-4 py-2 text-sm font-cinzel uppercase tracking-wide bg-sky-500 text-zinc-950 hover:bg-sky-400 rounded-sm disabled:opacity-50">{busy ? "Reconciling…" : "Confirm"}</button>
          </div>
        </Modal>
      )}

      <div className="mt-5 pt-4 border-t border-sky-900/30">
        <div className="flex items-center gap-2 mb-3">
          <Swords size={16} className="text-amber-400" />
          <h3 className="font-cinzel text-amber-300 uppercase tracking-widest text-sm">Reconcile Par Aequa Temps</h3>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-zinc-500 flex-1 min-w-[200px]">
            Ensures Par Aequa temporary gladiators never count toward a lanista's roster: <span className="text-amber-300">flags</span> any live temp missing the marker and <span className="text-amber-300">removes</span> stale temps from finished tournaments.
          </p>
          <button data-testid="repair-par-aequa-btn" onClick={repairParAequa} disabled={paBusy}
            className="px-5 py-2 font-cinzel uppercase tracking-wide text-sm rounded-sm bg-amber-500 text-zinc-950 hover:bg-amber-400 disabled:opacity-50">
            {paBusy ? "Reconciling…" : "Run Reconcile"}
          </button>
        </div>
        {paResult && (
          <p data-testid="repair-par-aequa-result" className="mt-3 text-sm text-zinc-300 tabular-nums">
            Live temps: <span className="text-amber-300 font-semibold">{paResult.live_temps}</span> ·
            flagged <span className="text-amber-300 font-semibold">{paResult.flagged}</span> ·
            removed stale <span className="text-amber-300 font-semibold">{paResult.deleted}</span>.
          </p>
        )}
      </div>
    </div>
  );
}
